import { createContext, useContext, useMemo } from 'react';
import { useAsync } from 'react-async';
import { useRealtime, userTopic, UserTopic } from '@koob/realtime';

export const NotificationsContext = createContext({});

export const useNotifications = () => useContext(NotificationsContext);

/**
 * Single source of notifications for both apps.
 *
 * There is no polling: the server pushes a ping over SSE and we refetch. The
 * `api` prop stays app-provided because the DMC and the TO axios clients carry
 * different interceptors (token refresh, impersonation, 401 handling).
 */
export default function NotificationsProvider({ api, children }) {
  const {
    data: notificationsData,
    isLoading: loading,
    reload: reloadNotifications,
  } = useAsync({ promiseFn: api.getNotifications });

  useRealtime(userTopic(UserTopic.NOTIFICATIONS), reloadNotifications);

  const notifications = useMemo(
    () => notificationsData?.data ?? [],
    [notificationsData],
  );

  const value = useMemo(
    () => ({
      notifications,
      loading,
      reloadNotifications,
      markAsRead: async (notificationId) => {
        await api.markNotificationAsRead({ notificationId });
        await reloadNotifications();
      },
      markAllAsRead: async () => {
        await api.markAllNotificationsAsRead();
        await reloadNotifications();
      },
    }),
    [api, notifications, loading, reloadNotifications],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}
